/**
 * ============================================================================
 * KbReviewPanel — Painel de revisão (aprovar/recusar + source_confidence).
 * ----------------------------------------------------------------------------
 * CHANGELOG:
 *   RELEASE: 2026-07-07 v1.0.0 - F1-Fix.
 * ============================================================================
 */
import { useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { useKbReviewDecision } from "@/hooks/kb/useKbEditorial";

interface Props {
  pageId: string;
  canReview: boolean;
  onDone?: () => void;
}

export function KbReviewPanel({ pageId, canReview, onDone }: Props) {
  const [comment, setComment] = useState("");
  const [confidence, setConfidence] = useState<number>(0.8);
  const decide = useKbReviewDecision();

  if (!canReview) {
    return (
      <div className="rounded-md border-l-4 border-primary bg-muted/40 p-3 text-sm">
        Aguardando revisão. Somente usuários com permissão <b>kb_review</b> podem aprovar ou recusar.
      </div>
    );
  }

  const submit = async (approve: boolean) => {
    await decide.mutateAsync({ page_id: pageId, approve, comment: comment || null, source_confidence: confidence });
    setComment("");
    onDone?.();
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="text-sm font-medium">Revisão</div>
      <div>
        <Label>Comentário</Label>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={2000}
          placeholder="Motivo da decisão, ajustes solicitados, etc." />
      </div>
      <div>
        <Label>Confiança da fonte: {confidence.toFixed(2)}</Label>
        <Slider value={[confidence]} min={0} max={1} step={0.05}
          onValueChange={(v) => setConfidence(v[0])} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => submit(false)} disabled={decide.isPending}>
          <X className="h-4 w-4 mr-1" /> Recusar
        </Button>
        <Button size="sm" onClick={() => submit(true)} disabled={decide.isPending}>
          <Check className="h-4 w-4 mr-1" /> Aprovar
        </Button>
      </div>
    </div>
  );
}
